import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import * as _ from 'lodash';

import { TeamnoteApiService } from '../../../../api/teamnote-api.service';
import { TeamNoteApiConstant } from '../../../../constants/api.constant';
import { MessageTypeConstant } from '../../../../constants/message-type.constant';
import { PresenceTypeConstant } from '../../../../constants/presence-type.constant';
import { LoggerService } from '../../../../utilities/logger/logger.service';
import { UtilitiesService } from '../../../../utilities/service/utilities.service';
import { ChatMessageService } from '../../../services/data/messages/chat-message/chat-message.service';
import { Message } from '../../../../models/message';
import { Chat } from '../../../../models/chat';

interface PinnedMessages {
  [chatId: string]: Message[];
}

const MAX_PAGE_SIZE = 100;

/**
 * Handles pinned-message (pin_message / unpin_message, type 204 / 205) traffic - see
 * dev_spec/chat-pin_message.
 *
 * The pinned set is server-authoritative (§7): a 204 / 205 carries no pin content, it only
 * signals that something changed for that chat. The client must always resync from
 * POST /messages/pinned rather than reconstructing state from the event stream. This service
 * therefore keeps a per-chat_id cache (pins are keyed on (chat, message), a message can belong
 * to several chats, so pin state is NEVER cached globally by message id) and exposes an
 * observable that the pinned banner / pinned list dialog / message context menu subscribe to.
 */
@Injectable()
export class PinnedMessageService {

  private _pinnedMessages: PinnedMessages = {};
  pinnedMessages$: BehaviorSubject<PinnedMessages> = new BehaviorSubject<PinnedMessages>({});

  constructor(
    private _teamnoteApiService: TeamnoteApiService,
    private _loggerService: LoggerService,
    private _utilitiesService: UtilitiesService,
    private _chatMessageService: ChatMessageService
  ) { }

  // ----- HTTP API -----

  /**
   * POST /messages/pin - idempotent, re-pinning an already-pinned message is a safe no-op
   * (the server keeps the original pinned_by / pinned_at and does not reorder it).
   */
  pinMessage(chatId: string, messageId: string, success?: Function, failure?: Function): void {
    if (!chatId || !messageId) {
      this._loggerService.warn('Trying to pin message without chat id / message id, rejected');
      return;
    }
    let url = TeamNoteApiConstant.MESSAGE_PIN.PIN;
    let params = {
      chat_id: chatId,
      message_id: messageId
    };
    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        this.refreshPinnedMessages(chatId);
        if (success) {
          success(resp);
        }
      },
      (err) => {
        this._loggerService.error('Pin message failed: ' + JSON.stringify(err));
        if (failure) {
          failure(err);
        }
      },
      false,
      false,
      true // isBackgroundTask, do not flash the global loading spinner
    );
  }

  /**
   * POST /messages/unpin - unlike pin, unpinning something that is NOT pinned returns 400.
   * Callers relying on this defensively should tolerate that 400; we still resync from the
   * server afterwards either way, so a stale local pin indicator self-heals.
   */
  unpinMessage(chatId: string, messageId: string, success?: Function, failure?: Function): void {
    if (!chatId || !messageId) {
      this._loggerService.warn('Trying to unpin message without chat id / message id, rejected');
      return;
    }
    let url = TeamNoteApiConstant.MESSAGE_PIN.UNPIN;
    let params = {
      chat_id: chatId,
      message_id: messageId
    };
    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        this.refreshPinnedMessages(chatId);
        if (success) {
          success(resp);
        }
      },
      (err) => {
        this._loggerService.warn('Unpin message failed (tolerated if already unpinned): ' + JSON.stringify(err));
        this.refreshPinnedMessages(chatId);
        if (failure) {
          failure(err);
        }
      },
      false,
      false,
      true
    );
  }

  /**
   * POST /messages/pinned - the authoritative pinned set for one page. Any chat member may call
   * this, including in chats where only admins may pin.
   */
  getPinnedMessages(chatId: string, size: number, page: number, success: Function, failure?: Function): void {
    if (!chatId) {
      this._loggerService.warn('Trying to get pinned messages without chat id, rejected');
      return;
    }
    let url = TeamNoteApiConstant.MESSAGE_PIN.PINNED;
    let params = {
      chat_id: chatId,
      size: size,
      page: page
    };
    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        success(resp);
      },
      (err) => {
        this._loggerService.error('Get pinned messages failed: ' + JSON.stringify(err));
        if (failure) {
          failure(err);
        }
      },
      false,
      false,
      true // isBackgroundTask, do not flash the global loading spinner on every re-fetch
    );
  }

  /**
   * Fetch the FULL pinned set for a chat and replace the cache entry for it.
   *
   * There is no cap on how many messages a chat may pin, so this pages with the hard max size
   * (100) until a short page comes back, rather than fetching page 0 only and silently missing
   * pins beyond it.
   */
  refreshPinnedMessages(chatId: string): void {
    if (!chatId) {
      return;
    }
    this.fetchAllPinnedPages(chatId, 0, []);
  }

  private fetchAllPinnedPages(chatId: string, page: number, accumulated: Message[]): void {
    this.getPinnedMessages(
      chatId,
      MAX_PAGE_SIZE,
      page,
      (msgs: Message[]) => {
        let all = accumulated.concat(msgs || []);
        if (msgs && msgs.length === MAX_PAGE_SIZE) {
          // page may not be the last one, keep loading
          this.fetchAllPinnedPages(chatId, page + 1, all);
        } else {
          this.setPinnedMessagesForChat(chatId, all);
        }
      },
      () => {
        // keep the previous cache on failure rather than wiping it out
      }
    );
  }

  private setPinnedMessagesForChat(chatId: string, msgs: any[]): void {
    // server already orders these pinned_at DESCENDING (most recently pinned first), keep that order
    let parsed = _.map(msgs, (m) => {
      let pm = this._chatMessageService.getSingleParsedMsg(_.cloneDeep(m));
      pm.pinned_by = m.pinned_by;
      pm.pinned_at = m.pinned_at;
      return pm;
    });
    this._pinnedMessages[chatId] = parsed;
    this.pinnedMessages$.next(this._pinnedMessages);
  }

  getPinnedMessagesForChat(chatId: string): Message[] {
    return this._pinnedMessages[chatId] || [];
  }

  checkIfMessagePinned(chatId: string, messageId: string): boolean {
    if (!chatId || !messageId) {
      return false;
    }
    return !!_.find(this._pinnedMessages[chatId], { message_id: messageId });
  }

  removePinnedMessagesCacheForChat(chatId: string): void {
    delete this._pinnedMessages[chatId];
    this.pinnedMessages$.next(this._pinnedMessages);
  }

  // ----- Incoming event handling (204 / 205) -----

  /**
   * A pin_message / unpin_message event carries no pin content (only actor + target_message_id),
   * so per §7.2 the client must re-fetch POST /messages/pinned to become correct for the chat(s)
   * it concerns - never reconstruct the pinned set from the event stream alone.
   */
  handlePinEventMessage(m: Message): void {
    _.each(m.chat_ids, (chatId) => {
      this.refreshPinnedMessages(chatId);
    });
  }

  // ----- Permission (§4) -----

  /**
   * Individual chats have no admin role at all - either participant may pin/unpin.
   * Group / announcement chats restrict pin/unpin to chat admins.
   * News channel / category do not support pinning.
   *
   * Pin and unpin are symmetric: base this purely on the viewer's CURRENT role, never on who
   * pinned the message.
   */
  checkIfCanPin(chat: Chat, userId: string): boolean {
    if (!chat) {
      return false;
    }
    switch (chat.t) {
      case PresenceTypeConstant.INDIVIDUAL_CHAT:
        return true;
      case PresenceTypeConstant.GROUP_CHAT:
      case PresenceTypeConstant.GROUP_BROADCAST:
        return !!chat.admins && chat.admins.indexOf(userId) !== -1;
      default:
        return false;
    }
  }

  /**
   * Reading the pinned set is open to all members regardless of who may pin (announcement
   * members still see the banner) - this only gates whether the chat TYPE supports pinning at
   * all (news channels / categories do not).
   */
  checkIfChatSupportsPin(chat: Chat): boolean {
    if (!chat) {
      return false;
    }
    return chat.t === PresenceTypeConstant.INDIVIDUAL_CHAT ||
      chat.t === PresenceTypeConstant.GROUP_CHAT ||
      chat.t === PresenceTypeConstant.GROUP_BROADCAST;
  }

  // ----- Eligibility (§5) -----

  /**
   * Only chat_text / chat_attachment / location / sticker / chat_poll may be pinned. Whisper
   * messages (received_by set) and expired / recalled messages are never pinnable.
   */
  checkIfMessagePinnable(m: Message): boolean {
    if (!m) {
      return false;
    }
    let pinnableTypes = [
      MessageTypeConstant.TEXT,
      MessageTypeConstant.ATTACHMENT,
      MessageTypeConstant.LOCATION,
      MessageTypeConstant.STICKER,
      MessageTypeConstant.CHAT_POLL
    ];
    if (_.indexOf(pinnableTypes, _.toInteger(m.type)) === -1) {
      return false;
    }
    if (m.received_by) {
      return false;
    }
    if (m.isRecalled || this._utilitiesService.checkIfMessageExpired(m.expiry_time)) {
      return false;
    }
    return true;
  }

}
